import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Link,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useTranslation } from "react-i18next";
import { ESLintError } from "../LintTriage";

interface FileErrorsViewProps {
  filePath: string;
  errors: ESLintError[];
  onBack: () => void;
  onErrorsRefresh?: () => void;
}

export const FileErrorsView: React.FC<FileErrorsViewProps> = ({
  filePath,
  errors,
  onBack,
}) => {
  const { t } = useTranslation();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const eslintCount = errors.filter((e) => e.source === "eslint").length;
  const typescriptCount = errors.filter((e) => e.source === "typescript")
    .length;

  const generateFilePrompt = () => {
    const errorList = errors
      .map((e, idx) => {
        const errorCode = e.errorCode || e.ruleId;
        return `${idx + 1}. Line ${e.line} - ${errorCode}: ${e.message}`;
      })
      .join("\n");

    return `You are an expert TypeScript developer fixing type errors in a React/TypeScript codebase.

FILE TO FIX: ${filePath}

ERRORS TO FIX (${errors.length} total):
${errorList}

INSTRUCTIONS:

1. READ THE FILE FIRST
   - Use the Read tool to read the entire file: ${filePath}
   - Understand what this file does and its role in the codebase
   - Identify any patterns or conventions used

2. FIX ERRORS METHODICALLY
   - Fix errors one at a time, starting from the top of the file
   - For each error:
     a) Understand the root cause (not just the symptom)
     b) Choose the most type-safe fix (avoid 'any', 'as any', or type assertions unless absolutely necessary)
     c) Prefer proper type definitions over quick fixes
     d) Make minimal changes - don't refactor unrelated code

3. COMMON FIX STRATEGIES
   - Missing properties: Add them to the interface/type definition
   - Type mismatches: Check if the types are correct, fix the source if needed
   - 'any' types: Replace with proper types from the codebase
   - Wrong arguments: Check function signature, pass correct parameters
   - Unsafe conversions: Use proper type guards or restructure the code

4. VERIFICATION (CRITICAL)
   After making fixes:
   - Run: npm run typecheck (must pass with 0 errors in this file)
   - Run: npm run test (if tests exist for this file)
   - Check: No new errors introduced in related files
   - Verify: The actual functionality is unchanged (behavior-preserving fixes only)

5. REPORT YOUR WORK
   When done, report:
   - Total errors fixed: X/${errors.length}
   - Any errors you couldn't fix (with explanation)
   - Typecheck status: PASS/FAIL
   - Test status: PASS/FAIL/SKIPPED

IMPORTANT CONSTRAINTS:
- Do NOT use 'as any' or '@ts-ignore' unless absolutely necessary
- Do NOT change functionality - only fix type errors
- Do NOT refactor code beyond what's needed for the fix
- DO maintain existing code style and conventions
- DO read the file first before making any changes

Start by reading ${filePath} to understand the code.`;
  };

  const copyAllPrompt = () => {
    navigator.clipboard.writeText(generateFilePrompt());
    setSnackbarMessage(t('screens.devTools.fileErrorsView.snackbar.copiedAll', { count: errors.length }));
    setSnackbarOpen(true);
  };

  const copyIndividualPrompt = (error: ESLintError) => {
    const errorCode = error.errorCode || error.ruleId;
    const prompt = `You are an expert TypeScript developer fixing a ${error.source || "code quality"} error.

FILE: ${error.relativeFilePath}
LINE: ${error.line}
ERROR: ${errorCode}
MESSAGE: ${error.message}

CODE CONTEXT:
\`\`\`typescript
${error.codeSnippet}
\`\`\`

INSTRUCTIONS:

1. READ THE FILE FIRST
   - Read ${error.relativeFilePath} to understand the full context
   - Don't just fix the line - understand why the error exists

2. APPLY TYPE-SAFE FIX
   - Avoid 'any' or type assertions unless absolutely necessary
   - Prefer fixing the root cause over suppressing the error
   - Make minimal changes - don't refactor unrelated code

3. VERIFY YOUR FIX
   - Run: npm run typecheck
   - Run: npm run test (if applicable)
   - Ensure no new errors introduced

Start by reading the file to understand the context.`;

    navigator.clipboard.writeText(prompt);
    setSnackbarMessage(t('screens.devTools.fileErrorsView.snackbar.copiedLine', { line: error.line }));
    setSnackbarOpen(true);
  };

  return (
    <Box>
      {/* Back button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        sx={{ mb: 2 }}
        variant="outlined"
      >
        {t('screens.devTools.fileErrorsView.buttons.backToFiles')}
      </Button>

      {/* File header */}
      <Typography variant="h6" gutterBottom>
        {filePath}
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom mb={2}>
        {eslintCount > 0 && `${eslintCount} ${t('screens.devTools.fileErrorsView.errorCounts.eslint')}`}
        {eslintCount > 0 && typescriptCount > 0 && ", "}
        {typescriptCount > 0 && `${typescriptCount} ${t('screens.devTools.fileErrorsView.errorCounts.typescript')}`}
        {" ("}
        {errors.length} {t('screens.devTools.fileErrorsView.errorCounts.total')})
      </Typography>

      {/* Copy all button */}
      <Button
        variant="contained"
        color="info"
        size="large"
        fullWidth
        startIcon={<ContentCopyIcon />}
        onClick={copyAllPrompt}
        sx={{ mb: 3 }}
      >
        {t('screens.devTools.fileErrorsView.buttons.copyAllErrors', { count: errors.length })}
      </Button>
      {/* Note: Use "Fix All with Claude" from file list for background execution */}

      {/* Error list */}
      <Stack spacing={2}>
        {errors.map((error, _idx) => (
          <Card key={error.id} variant="outlined">
            <CardContent>
              {/* Error header */}
              <Stack direction="row" spacing={1} mb={1}>
                {error.source && (
                  <Chip
                    label={error.source.toUpperCase()}
                    size="small"
                    color={error.source === "eslint" ? "primary" : "secondary"}
                  />
                )}
                <Chip
                  label={error.errorCode || error.ruleId}
                  size="small"
                  variant="outlined"
                />
                <Chip label={t('screens.devTools.fileErrorsView.errorCounts.line', { number: error.line })} size="small" />
              </Stack>

              {/* Error message */}
              <Typography variant="body2" gutterBottom>
                {error.message}
              </Typography>

              {/* Actions */}
              <Stack direction="row" spacing={1} mt={2}>
                <Link
                  href={`vscode://file${error.filePath}:${error.line}:${error.column}`}
                  variant="body2"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {t('screens.devTools.fileErrorsView.buttons.openInVSCode')}
                  <OpenInNewIcon sx={{ fontSize: 14 }} />
                </Link>
                <Typography variant="body2" color="text.secondary">
                  •
                </Typography>
                <Button
                  size="small"
                  onClick={() => copyIndividualPrompt(error)}
                  sx={{ minWidth: 0, p: 0, textTransform: "none" }}
                >
                  <Typography variant="body2">{t('screens.devTools.fileErrorsView.buttons.copyIndividual')}</Typography>
                </Button>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};
